import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import { useTheme } from '../../../shared/hooks/useTheme'
import { cn } from '@/lib/utils'

export function HeroSection() {
  const { t } = useTranslation()
  const { theme } = useTheme()

  return (
    <section className="relative mx-auto w-full max-w-7xl px-4 pt-20 pb-16 sm:px-6 sm:pt-28 sm:pb-20 lg:px-8 lg:pt-36 lg:pb-28">
      {/* Centered glow orb visual behind text */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
        <div
          className={cn(
            'h-[420px] w-[420px] rounded-full blur-[140px] landing-glow-a sm:h-[520px] sm:w-[520px]',
            'bg-[var(--glow-brand-strong)]',
          )}
        />
        <div
          className={cn(
            'absolute h-[280px] w-[280px] rounded-full blur-[100px] landing-glow-b sm:h-[340px] sm:w-[340px]',
            'bg-[var(--glow-secondary)]',
          )}
        />
      </div>

      <div className="relative z-10 mx-auto max-w-3xl text-center">
        {/* Badge with shimmer */}
        <span className="badge-shimmer bg-badge text-badge-foreground ring-badge-ring inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ring-1">
          {t('landing.hero.badge')}
        </span>

        {/* Headline with text gradient */}
        <h1
          className={cn(
            'm-0 mt-6 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl',
            theme === 'dark'
              ? 'bg-gradient-to-b from-zinc-100 via-zinc-300 to-zinc-500 bg-clip-text text-transparent'
              : 'bg-gradient-to-b from-zinc-900 via-zinc-700 to-zinc-500 bg-clip-text text-transparent',
          )}
        >
          {t('landing.hero.title')}
        </h1>

        {/* Subtitle */}
        <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          {t('landing.hero.subtitle')}
        </p>

        {/* CTAs */}
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a href="#contact" className="no-underline">
            <span className="btn-shimmer group inline-flex h-11 items-center justify-center gap-2 rounded-xl px-6 text-sm font-medium transition-all duration-200 bg-cta text-cta-foreground shadow-[var(--cta-shadow)] hover:bg-cta-hover">
              {t('landing.header.bookCall')}
              <ArrowRight
                size={14}
                className="transition-transform duration-200 group-hover:translate-x-0.5"
              />
            </span>
          </a>
          <a href="#platform" className="no-underline">
            <span className="inline-flex h-11 items-center justify-center rounded-xl px-6 text-sm font-medium text-muted-foreground ring-1 ring-border transition-colors duration-200 hover:bg-glass-hover hover:text-foreground">
              {t('landing.hero.secondaryCta')}
            </span>
          </a>
        </div>

        {/* Trust indicators — inline */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {[
            { label: t('landing.hero.stats.sources'), value: '4+' },
            { label: t('landing.hero.stats.tracked'), value: '24/7' },
            {
              label: t('landing.hero.stats.alerts'),
              value: t('landing.hero.stats.alertsValue'),
            },
          ].map((stat) => (
            <div key={stat.label} className="flex items-center gap-2">
              <span className={cn('h-1 w-1 rounded-full', 'bg-primary')} />
              <span className="text-sm font-medium text-foreground">{stat.value}</span>
              <span className="text-sm text-muted-foreground">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
